import { create } from 'zustand';
import { GAME_CONFIG } from '../config/gameConfig';

export const useGameStore = create((set, get) => ({
    // Game State
    gameState: GAME_CONFIG.STATES.MENU,
    setGameState: (state) => set({ gameState: state }),
    
    // Player State
    player: {
        health: GAME_CONFIG.PLAYER_MAX_HEALTH,
        coins: 0,
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 }
    },
    updatePlayer: (playerData) => set((state) => ({
        player: { ...state.player, ...playerData }
    })),
    
    // Game Progress
    score: 0,
    highScore: 0,
    updateScore: (newScore) => set((state) => ({
        score: newScore,
        highScore: Math.max(state.highScore, newScore)
    })),
    
    // Settings
    settings: {
        graphics: 'HIGH',
        masterVolume: GAME_CONFIG.MASTER_VOLUME,
        musicVolume: GAME_CONFIG.MUSIC_VOLUME,
        sfxVolume: GAME_CONFIG.SFX_VOLUME
    },
    updateSettings: (newSettings) => set((state) => ({
        settings: { ...state.settings, ...newSettings }
    })),
    
    // Game Reset
    resetGame: () => set({
        gameState: GAME_CONFIG.STATES.MENU,
        player: {
            health: GAME_CONFIG.PLAYER_MAX_HEALTH,
            coins: 0,
            position: { x: 0, y: 0, z: 0 },
            rotation: { x: 0, y: 0, z: 0 }
        },
        score: 0
    }),
    
    // Dashboard State
    dashboard: {
        userSolContributed: 2.5,
        totalReferrals: 8,
        totalEarnings: 250,
        daoPower: 185,
        progressiveJackpot: 15000,
        activePlayers: 1950,
        activeBorrowers: 320,
        activeStakers: 780,
        activeLenders: 450,
        recentActivity: [
            { id: 1, type: 'win', user: 'Player1', amount: 250, time: Date.now() - 120000 },
            { id: 2, type: 'stake', user: 'Player2', amount: 1000, time: Date.now() - 300000 },
            { id: 3, type: 'borrow', user: 'Player3', amount: 500, time: Date.now() - 600000 },
            { id: 4, type: 'lend', user: 'Player4', amount: 750, time: Date.now() - 900000 },
            { id: 5, type: 'win', user: 'Player5', amount: 1200, time: Date.now() - 1200000 }
        ],
        chartData: {
            '24h': Array.from({ length: 24 }, (_, i) => ({
                time: new Date(Date.now() - (23 - i) * 3600000).toISOString(),
                userSolContributed: 2.5 - Math.random() * 0.5,
                totalReferrals: Math.floor(8 - Math.random() * 2),
                totalEarnings: 250 - Math.random() * 50,
                daoPower: 185 - Math.random() * 20,
                progressiveJackpot: 15000 - Math.random() * 1000,
                activePlayers: 1950 - Math.floor(Math.random() * 100),
                activeBorrowers: 320 - Math.floor(Math.random() * 30),
                activeStakers: 780 - Math.floor(Math.random() * 50),
                activeLenders: 450 - Math.floor(Math.random() * 40)
            })),
            '7d': Array.from({ length: 7 }, (_, i) => ({
                time: new Date(Date.now() - (6 - i) * 86400000).toISOString(),
                userSolContributed: 2.5 - Math.random() * 1,
                totalReferrals: Math.floor(8 - Math.random() * 3),
                totalEarnings: 250 - Math.random() * 100,
                daoPower: 185 - Math.random() * 40,
                progressiveJackpot: 15000 - Math.random() * 2000,
                activePlayers: 1950 - Math.floor(Math.random() * 200),
                activeBorrowers: 320 - Math.floor(Math.random() * 60),
                activeStakers: 780 - Math.floor(Math.random() * 100),
                activeLenders: 450 - Math.floor(Math.random() * 80)
            })),
            '30d': Array.from({ length: 30 }, (_, i) => ({
                time: new Date(Date.now() - (29 - i) * 86400000).toISOString(),
                userSolContributed: 2.5 - Math.random() * 2,
                totalReferrals: Math.floor(8 - Math.random() * 5),
                totalEarnings: 250 - Math.random() * 200,
                daoPower: 185 - Math.random() * 80,
                progressiveJackpot: 15000 - Math.random() * 5000,
                activePlayers: 1950 - Math.floor(Math.random() * 500),
                activeBorrowers: 320 - Math.floor(Math.random() * 150),
                activeStakers: 780 - Math.floor(Math.random() * 300),
                activeLenders: 450 - Math.floor(Math.random() * 200)
            })),
            'all': Array.from({ length: 12 }, (_, i) => ({
                time: new Date(Date.now() - (11 - i) * 2592000000).toISOString(),
                userSolContributed: 2.5 - Math.random() * 2.4,
                totalReferrals: Math.floor(8 - Math.random() * 7),
                totalEarnings: 250 - Math.random() * 240,
                daoPower: 185 - Math.random() * 180,
                progressiveJackpot: 15000 - Math.random() * 14000,
                activePlayers: 1950 - Math.floor(Math.random() * 1900),
                activeBorrowers: 320 - Math.floor(Math.random() * 310),
                activeStakers: 780 - Math.floor(Math.random() * 770),
                activeLenders: 450 - Math.floor(Math.random() * 440)
            }))
        }
    },
    
    // Dashboard Actions
    updateDashboard: (dashboardData) => set((state) => ({
        dashboard: { ...state.dashboard, ...dashboardData }
    })),
    
    addActivity: (activity) => set((state) => ({
        dashboard: {
            ...state.dashboard,
            recentActivity: [
                { id: Date.now(), ...activity, time: Date.now() },
                ...state.dashboard.recentActivity
            ].slice(0, 20) // Keep only the 20 most recent activities
        }
    })),
    
    getTimeframeData: (timeframe = '24h') => {
        const { dashboard } = get();
        return dashboard.chartData[timeframe] || dashboard.chartData['24h'];
    }
}));
